import { useState } from 'react';
import { TENANT_ROLES } from '@provalia/shared';
import { useNavigate } from 'react-router-dom';
import { Card, Confirm, ErrorBox, Stat, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { SyncReportView } from '../../components/SyncReportView';
import { roleLabel } from '../../labels';
import type { SyncReport } from '../../types';
import type { TenantTabProps } from '../TenantDetail';
import { Checklist } from './Checklist';

export function SummaryTab(props: TenantTabProps): JSX.Element {
  const { slug, detail, reload } = props;
  const { overview, tenant } = detail;
  const toast = useToast();
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<unknown>(null);
  const [report, setReport] = useState<SyncReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const runSync = async (): Promise<void> => {
    setSyncing(true);
    setSyncError(null);
    try {
      const res = await api.post<{ report: SyncReport }>(`${tenantPath(slug)}/sync`);
      setReport(res.report);
      toast('Repaso de SAP terminado', 'ok');
      await reload();
    } catch (err) {
      setSyncError(err);
      setReport(null);
      // El repaso fallido también queda guardado como último repaso.
      void reload();
    } finally {
      setSyncing(false);
    }
  };

  const shown = report ?? overview.lastSync;
  const users = overview.users;
  const totalUsers = TENANT_ROLES.reduce((n, r) => n + (users[r]?.total ?? 0), 0);

  return (
    <div className="stack lg">
      <div className="grid cols-4">
        <Stat
          label="Proveedores"
          value={`${overview.suppliers.enabled} / ${overview.suppliers.total}`}
          foot="Habilitados / dados de alta"
        />
        <Stat label="Pedidos abiertos" value={overview.openOrders} foot="En seguimiento en el portal" />
        <Stat
          label="Correos"
          value={overview.outbox.sent7}
          foot={
            <>
              Enviados en 7 días · {overview.outbox.pending} pendientes ·{' '}
              <span className={overview.outbox.failed ? 'text-bad' : ''}>{overview.outbox.failed} fallidos</span>
            </>
          }
        />
        <Stat label="Usuarios" value={totalUsers} foot={`${tenant.companies.length} sociedad${tenant.companies.length === 1 ? '' : 'es'} de SAP`} />
      </div>

      <div className="grid cols-2 align-start">
        <Card title="Usuarios por perfil">
          <dl className="kv">
            {TENANT_ROLES.map((r) => (
              <div key={r} className="kv-row">
                <dt>{roleLabel(r)}</dt>
                <dd className="num">
                  {users[r]?.total ?? 0}
                  {users[r] && users[r].active !== users[r].total ? <span className="subtle"> · {users[r].active} activos</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card
          title="Repaso de SAP"
          actions={
            <button className="btn" onClick={() => void runSync()} disabled={syncing}>
              {syncing ? 'Repasando…' : 'Repasar SAP ahora'}
            </button>
          }
        >
          <div className="stack">
            <p className="subtle">
              Cada pocos minutos se leen los pedidos abiertos de SAP: se publican los nuevos, se detectan cambios y se mandan los avisos.
            </p>
            {syncError ? <ErrorBox error={syncError} /> : null}
            {shown ? (
              <>
                {report ? <div className="subtle">Resultado de este repaso:</div> : <div className="subtle">Último repaso:</div>}
                <SyncReportView report={shown} />
              </>
            ) : !syncError ? (
              <div className="callout">Todavía no se ha repasado nunca. Cuando la conexión esté probada, pulsa «Repasar SAP ahora».</div>
            ) : null}
          </div>
        </Card>
      </div>

      <Checklist {...props} />

      <section className="card danger-zone">
        <div className="card-head">
          <h2>Zona peligrosa</h2>
        </div>
        <div className="card-body row between">
          <div className="grow danger-text">
            <strong>Borrar cliente</strong>
            <p className="muted">
              Borra en Provalia sus usuarios, configuración, sociedades y todo el rastro de pedidos y avisos. En SAP no se toca nada: los
              pedidos y los campos U_EDI_ siguen allí. No se puede deshacer.
            </p>
          </div>
          <button className="btn danger" onClick={() => setDeleting(true)}>
            Borrar cliente
          </button>
        </div>
      </section>

      {deleting ? (
        <Confirm
          title={`Borrar ${tenant.name}`}
          danger
          confirmLabel="Borrar definitivamente"
          requireText={slug}
          message={
            <div className="stack sm">
              <p>
                Se borrará el esquema de datos de <strong>{tenant.name}</strong>: {totalUsers} usuarios, su configuración, sus{' '}
                {tenant.companies.length} sociedades y todo el seguimiento de pedidos y correos.
              </p>
              <p className="muted">Los datos de SAP no se tocan. El portal {tenant.portalUrl.replace(/^https?:\/\//, '')} dejará de funcionar.</p>
            </div>
          }
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await api.del(`${tenantPath(slug)}?confirm=${encodeURIComponent(slug)}`);
            toast(`Cliente ${tenant.name} borrado`, 'ok');
            navigate('/clientes', { replace: true });
          }}
        />
      ) : null}
    </div>
  );
}
